import { useMemo, useState } from 'react';
import { create } from 'zustand';
import type { Broadcast, BroadcastPlacement, MessagePreset, Widget } from '@noorview/core';
import { ACCENTS, MESSAGE_PRESETS, autoInk, gridMetrics, hexToRgb, isBroadcastLive, presetOf, rectToPx, rgbToHex, widgetName } from '@noorview/core';
import { attempt, backend, useApp } from '../data/store';
import { Sheet } from '../ui/controls';
import { Select } from '../ui/pickers';
import { AreaField, TextField } from '../ui/fields';
import { PositionPicker } from '../ui/PositionPicker';
import { Icon } from '../ui/icons';
import { openSheet } from '../app/ui';

/**
 * Announcement mode, from the Studio: turn a widget (or the whole screen, half of it, the middle or a band) into an
 * announcement on this screen for a set time, or until it's switched off. It goes live straight away, as a layer on
 * top; nothing in the design changes, so when it ends the screen is exactly as it was.
 */

import { ANNOUNCE_LENGTHS } from '../studio/ops';
export { ANNOUNCE_LENGTHS, KEEP_WIDGETS, canSwap } from '../studio/ops';

const left = (b: Broadcast, now: number) => {
  if (!b.endsAt) return 'until switched off';
  const s = Math.max(0, Math.round((Date.parse(b.endsAt) - now) / 1000));
  return s >= 3600 ? `${Math.floor(s / 3600)}h ${Math.round((s % 3600) / 60)}m left` : s >= 60 ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')} left` : `${s}s left`;
};

/** Whether the Studio canvas shows the screen's announcements over the design (hidden to edit underneath). */
export const useCanvasAnnouncements = create<{ hidden: boolean; set: (v: boolean) => void }>((set) => ({ hidden: false, set: (hidden) => set({ hidden }) }));

/** Announcements showing on this screen now, with a switch to take each one off. */
export function LiveAnnouncements({ screenId }: { screenId: string }) {
  const ws = useApp((s) => s.ws)!;
  const now = useApp((s) => s.now);
  const live = ws.broadcasts.filter((b) => b.kind === 'message' && isBroadcastLive(b, new Date(now), screenId));
  const hidden = useCanvasAnnouncements((s) => s.hidden), setHidden = useCanvasAnnouncements((s) => s.set);
  if (!live.length) return null;
  const screen = ws.screens.find((s) => s.id === screenId);
  const design = ws.designs.find((d) => d.id === screen?.designId);
  const widgetLabel = (b: Broadcast) => {
    const w = (design?.published ?? design?.draft)?.widgets.find((x) => x.id === b.message?.widgetId);
    return w ? `in ${widgetName(w)}` : placeLabel(b.placement);
  };
  return (
    <div className="ann-live" role="status">
      {live.map((b, i) => (
        <div key={b.id} className="ann-live-i">
          <i className="bc-dot" style={{ background: b.message?.color || presetOf(b.message?.preset).color }} />
          <span><b>{b.message?.heading}</b> is on the screen {widgetLabel(b)} · {left(b, now)}</span>
          <button type="button" className="btn sm secondary" onClick={() => openSheet({ name: 'editBroadcast', id: b.id })}>Edit</button>
          <button type="button" className="btn sm plain" onClick={() => attempt(() => backend.endBroadcast(b.id), 'Announcement switched off')}>Switch off</button>
          {i === 0 && <button type="button" className="btn sm plain" onClick={() => setHidden(!hidden)} aria-pressed={hidden}>{hidden ? 'Show on canvas' : 'Hide on canvas'}</button>}
        </div>
      ))}
    </div>
  );
}

const placeLabel = (p: BroadcastPlacement) => ({ full: 'full screen', half: 'on the right half', left: 'on the left half', center: 'in the middle', band: 'in a band at the bottom', top: 'in a band at the top', widget: 'in the announcements' })[p];

export function AnnounceSheet({ screenId, widgetId, onClose }: { screenId: string; widgetId?: string; onClose: () => void }) {
  const ws = useApp((s) => s.ws)!;
  const screen = ws.screens.find((s) => s.id === screenId);
  const rec = ws.designs.find((d) => d.id === screen?.designId);
  const draft = rec?.draft, live = rec?.published ?? null;
  const w = widgetId ? draft?.widgets.find((x) => x.id === widgetId) : undefined;
  const onScreen = !!(w && live?.widgets.some((x) => x.id === w.id && !x.hidden));
  const [placement, setPlacement] = useState<BroadcastPlacement>(w ? 'widget' : 'full');
  const [preset, setPreset] = useState<MessagePreset>('notice');
  const [heading, setHeading] = useState(presetOf('notice').heading);
  const [body, setBody] = useState('');
  const [color, setColor] = useState<string | null>(null);
  const [ink, setInk] = useState<string | null>(null);
  const [seconds, setSeconds] = useState(300);
  const [busy, setBusy] = useState(false);
  const p = presetOf(preset);
  const bg = color || p.color;
  const fg = ink || rgbToHex(autoInk(hexToRgb(bg)));
  // Where the chosen widget sits, for its picture in the position choices.
  const box = useMemo<[number, number, number, number] | undefined>(() => {
    if (!w || !draft) return undefined;
    const m = gridMetrics(draft), r = rectToPx(w, m);
    return [r.left / m.width, r.top / m.height, r.width / m.width, r.height / m.height];
  }, [w, draft]);
  const choose = (id: MessagePreset) => {
    const cur = presetOf(preset);
    if (!heading.trim() || heading === cur.heading) setHeading(presetOf(id).heading);
    if (!body.trim() || body === cur.body) setBody(presetOf(id).body);
    setPreset(id);
    setColor(null);
  };
  const show = async () => {
    if (!heading.trim()) return;
    setBusy(true);
    const start = new Date();
    const ok = await attempt(() => backend.startBroadcast({
      kind: 'message', screenIds: [screenId], placement, startsAt: start.toISOString(), endsAt: seconds > 0 ? new Date(start.getTime() + seconds * 1000).toISOString() : null,
      message: { preset, heading: heading.trim(), body: body.trim(), ...(placement === 'widget' && w ? { widgetId: w.id } : {}), ...(color ? { color } : {}), ...(ink ? { textColor: ink } : {}) },
    }), `Announcement on ${screen?.name ?? 'the screen'}`);
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Sheet open onClose={onClose} title={w ? `Switch ${widgetName(w)} to an announcement` : 'Announcement'} subtitle={`Shows on ${screen?.name ?? 'this screen'} straight away, on top of the design. When it ends, the screen goes back to how it was.`} dock
      footer={<><button type="button" className="btn plain" onClick={onClose}>Cancel</button>
        <button type="button" className={`btn ${preset === 'emergency' ? 'danger' : 'primary'}`} disabled={busy || !heading.trim()} onClick={show}>{busy ? 'Showing…' : 'Show it now'}</button></>}>
      <div className="field"><span>Where it shows</span>
        <PositionPicker value={placement} onChange={setPlacement} widget={w ? { label: 'This widget only', box } : undefined} />
        {placement === 'widget' && w && !onScreen && <small className="hint">This widget isn’t on the screen yet (send your design live first), so the announcement shows in the middle instead.</small>}
      </div>
      <div className="bc-presets" role="radiogroup" aria-label="Kind of announcement">
        {MESSAGE_PRESETS.map((x) => <button key={x.id} type="button" role="radio" aria-checked={preset === x.id} className={`bc-preset${preset === x.id ? ' on' : ''}`} style={{ ['--bm' as string]: x.color }} onClick={() => choose(x.id)}>{x.label}</button>)}
      </div>
      <TextField label="Heading" value={heading} max={60} onChange={setHeading} />
      <AreaField label="Message" value={body} max={240} rows={3} onChange={setBody} />
      <div className="two">
        <div className="field"><span>Colour</span>
          <div className="swatches" role="radiogroup" aria-label="Colour">
            <button type="button" role="radio" aria-checked={!color} className={`sw kind${!color ? ' on' : ''}`} style={{ ['--sw' as string]: p.color }} onClick={() => setColor(null)} title={`${p.label} colour`}><span>A</span></button>
            {([...ACCENTS.filter((c) => c !== '#FFFFFF'), '#000000'] as string[]).map((c) => <button key={c} type="button" role="radio" aria-checked={color === c} aria-label={c} className={`sw${color === c ? ' on' : ''}`} style={{ ['--sw' as string]: c }} onClick={() => setColor(c)} />)}
          </div>
        </div>
        <div className="field"><span>Text colour</span>
          <div className="swatches" role="radiogroup" aria-label="Text colour">
            <button type="button" role="radio" aria-checked={!ink} className={`sw kind${!ink ? ' on' : ''}`} style={{ ['--sw' as string]: fg }} onClick={() => setInk(null)} title="Automatic: black or white, whichever reads best"><span>A</span></button>
            {['#FFFFFF', '#000000', '#FFD60A', '#FF453A'].map((c) => <button key={c} type="button" role="radio" aria-checked={ink === c} aria-label={c} className={`sw${ink === c ? ' on' : ''}`} style={{ ['--sw' as string]: c }} onClick={() => setInk(c)} />)}
          </div>
        </div>
      </div>
      <div className="field"><span>For how long</span><Select label="For how long" value={String(seconds)} onChange={(v) => setSeconds(Number(v))} options={ANNOUNCE_LENGTHS.map(([v, l]) => ({ value: String(v), label: l }))} /></div>
      <div className="bc-preview" style={{ ['--bm' as string]: bg, color: fg }} aria-hidden><small>{p.label}</small><b>{heading || p.heading}</b>{body && <span>{body}</span>}</div>
      <p className="hint" style={{ margin: 0 }}><Icon name="clock" size={14} /> If it covers the clock, the time stays in the top corner so everyone can still see it.</p>
    </Sheet>
  );
}
